import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import { Interval } from 'luxon';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { OrderPunctualityType, RejectionReason } from 'apis/merchant-gateway/insights';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { LegendChartType } from 'modules/experience-core';

import ErrorPage from '../components/ErrorPage';
import ErrorView from '../components/ErrorView';
import LoadingPage from '../components/LoadingPage';
import LoadingView from '../components/LoadingView';
import OfflineTimeLegacy from '../components/operations/OfflineTimeLegacy';
import OperationsChartSelector from '../components/operations/OperationsChartSelector';
import OperationsPunctualityChart from '../components/operations/OperationsPunctualityChart';
import OperationsRejectionsChart from '../components/operations/OperationsRejectionsChart';
import OperationsSummary from '../components/operations/OperationsSummary';
import { PreparationTimeChart } from '../components/operations/PreparationTimeChart';
import PunctualitySummaryTable from '../components/operations/PunctualityTable';
import RejectionsCategoriesTable from '../components/operations/RejectionsCategoriesTable';
import OfflineTime from '../components/operations/offlineTime/OfflineTime';
import { OperationsChartCategory } from '../components/operations/types';
import useNewAlivenessFeatureFlag from '../hooks/useAlivenessFeatureFlag';
import useDeliveryTypeChecks from '../hooks/useDeliveryTypeChecks';
import {
  useOfflineTimeInsightWithPreviousInterval,
  usePunctualityInsightWithPreviousInterval,
  useRejectionCategoriesInsight,
  useRejectionsInsightWithPreviousInterval,
} from '../hooks/useInsightApi';
import { useTelemetryBusinessInsightsV2OperationsView } from '../telemetry';
import { InsightEntity } from '../types';
import { isAllError, isInitialLoading } from '../utils';

type OperationsPageProps = {
  insightEntity: InsightEntity;
  dateInterval: Interval;
  isVATEnabled: boolean;
};

const OperationsPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 16px;
    gap: 16px;
  }
`;

const OperationsSummaryAndChartContainer = styled.div`
  position: relative;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;
  min-height: 417px;
  display: flex;
  flex-direction: column;
`;

const Divider = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const ChartWrapper = styled.div`
  width: 100%;
`;

const OperationsTablesContainer = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
  }
`;

const OperationsTable = styled.div`
  flex: 1;
`;

const OperationsPage = ({ insightEntity, dateInterval, isVATEnabled }: OperationsPageProps) => {
  const { formatMessage } = useIntl();
  useTelemetryBusinessInsightsV2OperationsView(insightEntity.type);
  const punctualityLegends = {
    [OrderPunctualityType.EARLY]: {
      value: formatMessage({ id: 'insights.punctuality.orders-early' }),
      color: colors.wolt88,
      type: LegendChartType.BAR,
    },
    [OrderPunctualityType.ON_TIME]: {
      value: formatMessage({ id: 'insights.punctuality.orders-on-time' }),
      color: colors.wolt32,
      type: LegendChartType.BAR,
    },
    [OrderPunctualityType.LATE]: {
      value: formatMessage({ id: 'insights.punctuality.orders-late' }),
      color: colors.orange48,
      type: LegendChartType.BAR,
    },
  };

  const rejectionsLegends = {
    [RejectionReason.AUTOMATIC]: {
      value: formatMessage({ id: 'insights.rejections.auto' }),
      color: colors.orange48,
      type: LegendChartType.BAR,
    },
    [RejectionReason.VENUE]: {
      value: formatMessage({ id: 'insights.rejections.venue' }),
      color: colors.wolt88,
      type: LegendChartType.BAR,
    },
    [RejectionReason.OTHER]: {
      value: formatMessage({ id: 'insights.rejections.other' }),
      color: colors.wolt32,
      type: LegendChartType.BAR,
    },
    LOSS: {
      value: formatMessage({ id: 'insights.operations.loss-from-rejections' }),
      color: colors.strawberry100,
      type: LegendChartType.LINE,
    },
  };

  const preparationTimeLegends = {
    avgPreparationTimeSec: {
      value: formatMessage({ id: 'insights.operations.preparation-time' }),
      color: colors.wolt88,
      type: LegendChartType.BAR,
    },
  };

  const [selectedCategory, setSelectedCategory] = useState<OperationsChartCategory>(OperationsChartCategory.REJECTIONS);

  const { isNewAlivenessEnabled, isLoading: isFeatureFlagLoading } = useNewAlivenessFeatureFlag(
    insightEntity.type,
    insightEntity.id,
  );

  const { isSelfDeliveryOnlyVenue, isHybridDeliveryVenue } = useDeliveryTypeChecks(insightEntity);

  const rejectionsInsight = useRejectionsInsightWithPreviousInterval({
    insightEntity,
    dateInterval,
  });

  const punctualityInsight = usePunctualityInsightWithPreviousInterval({
    insightEntity,
    dateInterval,
  });

  const rejectionCategoriesInsight = useRejectionCategoriesInsight(insightEntity, dateInterval);

  const offlineTimeInsight = useOfflineTimeInsightWithPreviousInterval({
    insightEntity,
    dateInterval,
  });

  const allInsightResponses = [rejectionsInsight, punctualityInsight, offlineTimeInsight, rejectionCategoriesInsight];

  if (isInitialLoading(allInsightResponses) || isFeatureFlagLoading) {
    return <LoadingPage />;
  }
  if (isAllError(allInsightResponses)) {
    return (
      <ErrorPage
        onRetry={() => {
          allInsightResponses.forEach(insight => insight.refetch());
        }}
      />
    );
  }

  return (
    <OperationsPageContainer>
      {
        <>
          <OperationsSummaryAndChartContainer>
            {punctualityInsight.isFetching || rejectionsInsight.isFetching ? (
              <LoadingView />
            ) : punctualityInsight.isError || rejectionsInsight.isError ? (
              <ErrorView
                onRetry={() => {
                  punctualityInsight.isError && punctualityInsight.refetch();
                  rejectionsInsight.isError && rejectionsInsight.refetch();
                }}
              />
            ) : (
              <>
                <OperationsSummary
                  rejectionsInsight={rejectionsInsight}
                  punctualityInsight={punctualityInsight}
                  currency={rejectionsInsight.selected.data?.currency}
                  isVATEnabled={isVATEnabled}
                  legends={Object.values(
                    selectedCategory === OperationsChartCategory.PUNCTUALITY
                      ? punctualityLegends
                      : selectedCategory === OperationsChartCategory.REJECTIONS
                        ? rejectionsLegends
                        : preparationTimeLegends,
                  )}
                  hidePunctualityAndPrepTime={isSelfDeliveryOnlyVenue}
                  showHybridDeliveryDisclaimer={isHybridDeliveryVenue}
                />
                <Divider />
                <ChartWrapper>
                  <OperationsChartSelector
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    unavailableCategories={
                      isSelfDeliveryOnlyVenue
                        ? [OperationsChartCategory.PUNCTUALITY, OperationsChartCategory.PREPARATION_TIME]
                        : []
                    }
                  />
                  {selectedCategory === OperationsChartCategory.PUNCTUALITY && (
                    <OperationsPunctualityChart
                      legends={punctualityLegends}
                      data={punctualityInsight.selected.data?.data.punctuality?.split || []}
                      summaryKey="insights.performance.total-orders"
                    />
                  )}
                  {selectedCategory === OperationsChartCategory.REJECTIONS && (
                    <OperationsRejectionsChart
                      legends={rejectionsLegends}
                      data={rejectionsInsight.selected.data?.data.rejections?.split || []}
                      currency={{
                        [LegendChartType.LINE]: rejectionsInsight.selected.data?.currency,
                      }}
                      summaryKey="insights.rejections.header"
                      isVATEnabled={isVATEnabled}
                    />
                  )}
                  {selectedCategory === OperationsChartCategory.PREPARATION_TIME && (
                    <PreparationTimeChart
                      legends={preparationTimeLegends}
                      data={punctualityInsight.selected.data?.data.punctuality?.split || []}
                      summaryKey="insights.operations.preparation-time"
                    />
                  )}
                </ChartWrapper>
              </>
            )}
          </OperationsSummaryAndChartContainer>
          <OperationsTablesContainer>
            <OperationsTable>
              <RejectionsCategoriesTable
                rejectionCategoriesInsight={rejectionCategoriesInsight}
                insightEntityType={insightEntity.type}
              />
            </OperationsTable>
            {!isSelfDeliveryOnlyVenue && (
              <OperationsTable>
                <PunctualitySummaryTable
                  punctualityInsight={punctualityInsight}
                  title={
                    isHybridDeliveryVenue
                      ? formatMessage({ id: 'insights.punctuality.title-punctuality-for-wolt-deliveries' })
                      : formatMessage({ id: 'insights.punctuality.title' })
                  }
                  tooltip={
                    isHybridDeliveryVenue
                      ? formatMessage({ id: 'insights.punctuality.tooltip-hybrid-delivery' })
                      : formatMessage({ id: 'insights.punctuality.tooltip' })
                  }
                />
              </OperationsTable>
            )}
          </OperationsTablesContainer>
          {isNewAlivenessEnabled ? (
            <OfflineTime offlineTimeInsight={offlineTimeInsight} insightEntityType={insightEntity.type} />
          ) : (
            <OfflineTimeLegacy offlineTimeInsight={offlineTimeInsight} />
          )}
        </>
      }
    </OperationsPageContainer>
  );
};

export default OperationsPage;
